import { AlertDialog, Button, SimpleSpinner } from '@buffer-mono/popcorn'

import type { DismissActionType } from './types'
import { useResolveMentionsDialog } from './useResolveMentionsDialog'

import type { JSX, ReactNode } from 'react'

type ActionConfig = {
  title: string
  fallbackButtonText: string
  fallbackDescription: string
  emptyDesc: string
  contextPhrase: string
}

// Mentions bulk-resolve only offers 'all' and 'older-than-month'; there is no
// post-scoped variant like comments.
const ACTION_CONFIG: Record<
  Exclude<DismissActionType, 'post'>,
  ActionConfig
> = {
  'older-than-month': {
    title: 'Resolve Mentions Older Than 1 Month',
    fallbackButtonText: 'Resolve Mentions',
    fallbackDescription:
      "All mentions older than 1 month will be marked as resolved. You will be able to access them using the 'status' filter.",
    emptyDesc:
      'You currently have no unanswered mentions older than 1 month that could be resolved.',
    contextPhrase: 'older than 1 month ',
  },
  all: {
    title: 'Resolve All Mentions',
    fallbackButtonText: 'Resolve All Mentions',
    fallbackDescription:
      "All your mentions will be marked as resolved. You will be able to access them using the 'status' filter.",
    emptyDesc:
      'You currently have no unanswered mentions that could be resolved.',
    contextPhrase: '',
  },
}

type ResolveMentionsDialogProps = {
  channelIds: string[]
  actionType: DismissActionType
  onHideTooltip?: () => void
  shouldOpen?: boolean
  onOpenChange?: (open: boolean) => void
  onResolveMentions?: () => void
  onResolvingChange?: (isResolving: boolean) => void
}

export const ResolveMentionsDialog = ({
  channelIds,
  actionType,
  onHideTooltip,
  shouldOpen = false,
  onOpenChange,
  onResolveMentions,
  onResolvingChange,
}: ResolveMentionsDialogProps): JSX.Element | null => {
  const {
    isOpen,
    handleOpenChange,
    handleResolveMentions,
    isResolving,
    mentionCount,
    isLoadingCount,
    errorCount,
    effectiveLimit,
  } = useResolveMentionsDialog({
    channelIds,
    actionType,
    shouldOpen,
    onOpenChange,
    onHideTooltip,
    onResolvingChange,
  })

  if (!isOpen) {
    return null
  }

  // 'post' isn't offered for mentions; fall back to the 'all' copy defensively.
  const cfg =
    actionType === 'post' ? ACTION_CONFIG.all : ACTION_CONFIG[actionType]

  // The backend caps the count per channel and echoes the cap back, so a count
  // that reaches it is a floor rather than the real total.
  const limitReached =
    effectiveLimit !== null &&
    mentionCount !== null &&
    mentionCount >= effectiveLimit

  const displayCount = limitReached ? `+${effectiveLimit}` : mentionCount
  const pluralSuffix = mentionCount === 1 ? '' : 's'

  const getDialogContent = (): {
    description: ReactNode
    buttonText: string
  } => {
    if (isLoadingCount || mentionCount === null) {
      return {
        description: errorCount ? (
          cfg.fallbackDescription
        ) : (
          <SimpleSpinner aria-label="Loading mention count" />
        ),
        buttonText: cfg.fallbackButtonText,
      }
    }

    if (mentionCount === 0) {
      return { description: cfg.emptyDesc, buttonText: 'Got It' }
    }

    return {
      description: (
        <>
          <output aria-live="polite" aria-atomic="true">
            <strong>
              {displayCount} mention{pluralSuffix}
            </strong>{' '}
            {cfg.contextPhrase}will be marked as resolved.{' '}
          </output>
          You will be able to access them using the &apos;status&apos; filter.
        </>
      ),
      buttonText: `Resolve ${displayCount} Mention${pluralSuffix}`,
    }
  }

  const { description, buttonText } = getDialogContent()

  return (
    <AlertDialog open={true} onOpenChange={handleOpenChange}>
      <AlertDialog.Content>
        <AlertDialog.Title>{cfg.title}</AlertDialog.Title>
        <AlertDialog.Description>{description}</AlertDialog.Description>
        <AlertDialog.Actions>
          {mentionCount === 0 ? (
            <AlertDialog.Cancel>
              <Button
                variant="primary"
                onClick={(): void => handleOpenChange(false)}
              >
                Got It
              </Button>
            </AlertDialog.Cancel>
          ) : (
            <>
              <AlertDialog.Cancel>
                <Button variant="tertiary">Cancel</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button
                  variant="critical"
                  disabled={isResolving || (isLoadingCount && !errorCount)}
                  onClick={(): void => {
                    void handleResolveMentions()
                    onResolveMentions?.()
                  }}
                >
                  {buttonText}
                </Button>
              </AlertDialog.Action>
            </>
          )}
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
